import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SiretLookup from './SiretLookup';
import './FichierPro.css';
import Encaisser from './Encaisser';

const API_URL = process.env.REACT_APP_API_URL || '';

const categories = [
  'Restaurant', 'Pizzeria', 'Kebab', 'Traiteur',
  'Cafe', 'Bar',
  'Boulangerie', 'Patisserie', 'Chocolatier', 'Fromagerie', 'Poissonnier',
  'Salon de coiffure', 'Institut de beaute',
  'Epicerie fine', 'Supermarche', 'Boucherie', 'Fleuriste',
  'Pharmacie', 'Opticien', 'Dentiste', 'Medecin', 'Veterinaire',
  'Boutique vetements', 'Bijouterie',
  'Agence immobiliere', 'Banque', 'Assurance',
  'Garage auto', 'Caviste', 'Pressing', 'Librairie',
  'Tabac Presse', 'Hotel',
  'Magasin informatique', 'Plombier Electricien',
  'Serrurier', 'Peintre', 'Photographe', 'Tatoueur',
  'Auto-ecole', 'Salle de sport'
];

const villes = [
  'Paris',
  '92 - Hauts-de-Seine',
  '93 - Seine-Saint-Denis',
  '94 - Val-de-Marne',
  '91 - Essonne',
  '77 - Seine-et-Marne',
  '78 - Yvelines',
  '95 - Val-d\'Oise',
  // Grandes villes France
  'Marseille', 'Lyon', 'Toulouse', 'Nice',
  'Nantes', 'Strasbourg', 'Montpellier', 'Bordeaux', 'Lille',
  'Rennes', 'Reims', 'Le Havre', 'Saint-Etienne', 'Toulon',
  'Grenoble', 'Dijon', 'Angers', 'Nimes', 'Clermont-Ferrand',
  'Aix-en-Provence', 'Brest', 'Metz', 'Besancon', 'Perpignan',
  'Orleans', 'Rouen', 'Mulhouse', 'Caen', 'Nancy'
];

const arrondissementsMap = {
  'Paris': Array.from({ length: 20 }, (_, i) => ({
    label: `${i + 1}${i === 0 ? 'er' : 'ème'} arrondissement`,
    code: `750${String(i + 1).padStart(2, '0')}`
  })),
  'Marseille': Array.from({ length: 16 }, (_, i) => ({
    label: `${i + 1}${i === 0 ? 'er' : 'ème'} arrondissement`,
    code: `130${String(i + 1).padStart(2, '0')}`
  })),
  'Lyon': Array.from({ length: 9 }, (_, i) => ({
    label: `${i + 1}${i === 0 ? 'er' : 'ème'} arrondissement`,
    code: `6900${i + 1}`
  })),
  '92 - Hauts-de-Seine': [
    'Boulogne-Billancourt', 'Nanterre', 'Courbevoie', 'Colombes', 'Asnieres-sur-Seine',
    'Rueil-Malmaison', 'Levallois-Perret', 'Issy-les-Moulineaux', 'Neuilly-sur-Seine',
    'Antony', 'Clamart', 'Montrouge'
  ].map(c => ({ label: c, code: c })),
  '93 - Seine-Saint-Denis': [
    'Saint-Denis', 'Montreuil', 'Aubervilliers', 'Aulnay-sous-Bois', 'Drancy',
    'Noisy-le-Grand', 'Pantin', 'Bondy', 'Bobigny', 'Saint-Ouen', 'Epinay-sur-Seine', 'Sevran'
  ].map(c => ({ label: c, code: c })),
  '94 - Val-de-Marne': [
    'Creteil', 'Vitry-sur-Seine', 'Champigny-sur-Marne', 'Ivry-sur-Seine', 'Maisons-Alfort',
    'Saint-Maur-des-Fosses', 'Villejuif', 'Vincennes', 'Fontenay-sous-Bois', 'Alfortville'
  ].map(c => ({ label: c, code: c })),
  '91 - Essonne': [
    'Evry-Courcouronnes', 'Corbeil-Essonnes', 'Massy', 'Savigny-sur-Orge',
    'Sainte-Genevieve-des-Bois', 'Viry-Chatillon', 'Palaiseau', 'Athis-Mons', 'Yerres'
  ].map(c => ({ label: c, code: c })),
  '77 - Seine-et-Marne': [
    'Meaux', 'Chelles', 'Melun', 'Pontault-Combault', 'Savigny-le-Temple',
    'Torcy', 'Roissy-en-Brie', 'Combs-la-Ville', 'Lagny-sur-Marne'
  ].map(c => ({ label: c, code: c })),
  '78 - Yvelines': [
    'Versailles', 'Sartrouville', 'Mantes-la-Jolie', 'Saint-Germain-en-Laye', 'Poissy',
    'Conflans-Sainte-Honorine', 'Montigny-le-Bretonneux', 'Plaisir', 'Trappes'
  ].map(c => ({ label: c, code: c })),
  '95 - Val-d\'Oise': [
    'Argenteuil', 'Sarcelles', 'Cergy', 'Garges-les-Gonesse', 'Franconville',
    'Goussainville', 'Bezons', 'Ermont', 'Taverny', 'Herblay'
  ].map(c => ({ label: c, code: c }))
};

const calculateEndTime = (startTime) => {
  const [hours, minutes] = startTime.split(':').map(Number);
  const endHours = hours + 1;
  return `${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

function FichierPro({ currentUser }) {
  const [mode, setMode] = useState('search'); // 'search' ou 'base'
  const [activite, setActivite] = useState('');
  const [ville, setVille] = useState('');
  const [arrondissement, setArrondissement] = useState('');
  const [searchText, setSearchText] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [baseTotalPages, setBaseTotalPages] = useState(1);
  const [detailFiche, setDetailFiche] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [searchDone, setSearchDone] = useState(false);
  const [sources, setSources] = useState({ osm: 0, google: 0 });
  const [dbStats, setDbStats] = useState({ total: 0, byActivite: [], byVille: [] });
  const [googleApiCalls, setGoogleApiCalls] = useState(0);
  const [googleApiMois, setGoogleApiMois] = useState('');
  const [villesConfig, setVillesConfig] = useState([]);
  const [showVillesConfig, setShowVillesConfig] = useState(false);

  // Config API Google states
  const [apiUsage, setApiUsage] = useState(null);
  const [editBudget, setEditBudget] = useState('');
  const [editAlertEmail, setEditAlertEmail] = useState('');
  const [populateRunning, setPopulateRunning] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [populateMessage, setPopulateMessage] = useState('');
  const [maxCombinations, setMaxCombinations] = useState('');

  // RDV Modal state
  const [showModal, setShowModal] = useState(false);
  const [commerciaux, setCommerciaux] = useState([]);
  const [rdvCreated, setRdvCreated] = useState({});
  const [formData, setFormData] = useState({
    commercial_id: '',
    titre: '',
    description: '',
    client_nom: '',
    client_telephone: '',
    client_adresse: '',
    date_rdv: '',
    heure_debut: '09:00',
    heure_fin: '10:00',
    statut: 'planifie'
  });

  const ITEMS_PER_PAGE = 50;

  // Charger les commerciaux + stats au mount
  useEffect(() => {
    const fetchCommerciaux = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/planning/commerciaux`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCommerciaux(response.data);
      } catch (err) {
        console.error('Erreur chargement commerciaux:', err);
      }
    };
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/fichier-pro/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDbStats(prev => ({ ...prev, total: response.data.total }));
        if (response.data.googleApiCalls !== undefined) {
          setGoogleApiCalls(response.data.googleApiCalls);
          setGoogleApiMois(response.data.googleApiMois || '');
        }
      } catch (err) {
        console.error('Erreur chargement stats:', err);
      }
    };
    const fetchVilles = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/fichier-pro/villes`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setVillesConfig(response.data);
      } catch (err) {
        console.error('Erreur chargement villes:', err);
      }
    };
    fetchCommerciaux();
    fetchStats();
    fetchVilles();
  }, []);

  // Charger l'usage API Google (admin only)
  const fetchApiUsage = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/fichier-pro/api-usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiUsage(response.data);
      setPopulateRunning(response.data.populate?.running || false);
      if (editBudget === '') setEditBudget(String(response.data.budget?.total || 254));
      if (editAlertEmail === '') setEditAlertEmail(response.data.budget?.alertEmail || '');
    } catch (err) {
      console.error('Erreur chargement API usage:', err);
    }
  };

  useEffect(() => {
    if (currentUser && currentUser.role === 'admin') {
      fetchApiUsage();
      const interval = setInterval(fetchApiUsage, 5000);
      return () => clearInterval(interval);
    }
  }, [currentUser]);

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/fichier-pro/api-config`, {
        budget: parseFloat(editBudget),
        alertEmail: editAlertEmail
      }, { headers: { Authorization: `Bearer ${token}` } });
      await fetchApiUsage();
      alert('Configuration sauvegardée');
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingConfig(false);
    }
  };

  const handleStartPopulate = async () => {
    if (!window.confirm('Lancer l\'enrichissement de la base ? Cela va parcourir toutes les combinaisons activité × ville.')) return;
    try {
      const token = localStorage.getItem('token');
      const body = {};
      if (maxCombinations && parseInt(maxCombinations) > 0) {
        body.limit = parseInt(maxCombinations);
      }
      const response = await axios.post(`${API_URL}/api/fichier-pro/populate-all`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPopulateMessage(response.data.message);
      setPopulateRunning(true);
      setTimeout(fetchApiUsage, 2000);
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setPopulateMessage('Erreur: ' + msg);
    }
  };

  const handleStopPopulate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/fichier-pro/stop-populate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPopulateMessage(response.data.message);
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.error || err.message));
    }
  };

  const toggleVille = async (villeName, currentActive) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/fichier-pro/villes/toggle`, {
        ville: villeName,
        active: !currentActive
      }, { headers: { Authorization: `Bearer ${token}` } });
      setVillesConfig(prev => prev.map(v => v.ville === villeName ? { ...v, active: !currentActive ? 1 : 0 } : v));
    } catch (err) {
      console.error('Erreur toggle ville:', err);
    }
  };

  // Villes disponibles selon le rôle (Paris toujours en premier)
  const villesDisponibles = currentUser && currentUser.role === 'admin'
    ? villes
    : villesConfig.filter(v => v.active).map(v => v.ville).sort((a, b) => a === 'Paris' ? -1 : b === 'Paris' ? 1 : 0);

  const handleStatutChange = async (telephoneClean, newStatut) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/fichier-pro/statut`, {
        telephone_clean: telephoneClean,
        statut: newStatut
      }, { headers: { Authorization: `Bearer ${token}` } });
      const isExclusif = ['a_rappeler', 'interesse', 'pas_interesse', 'rdv_pris', 'ne_pas_rappeler', 'nrp'].includes(newStatut);
      setResults(prev => prev.map(r =>
        r.telephone_clean === telephoneClean ? {
          ...r,
          statut: newStatut,
          statut_par: isExclusif ? currentUser.id : null,
          statut_par_nom: isExclusif ? currentUser.nom : null,
          statut_date: isExclusif ? new Date().toISOString() : null
        } : r
      ));
    } catch (err) {
      console.error('Erreur changement statut:', err);
      if (err.response && err.response.status === 403) {
        alert(err.response.data.error || 'Cette fiche est assignée à un autre utilisateur');
      }
    }
  };

  const statutLabels = {
    nouveau: 'Nouveau',
    a_rappeler: 'A rappeler',
    interesse: 'Intéressé',
    pas_interesse: 'Pas intéressé',
    rdv_pris: 'RDV pris',
    ne_pas_rappeler: 'Ne pas rappeler',
    nrp: 'NRP'
  };

  // Recherche API (OSM + Google)
  const handleSearch = async () => {
    if (!activite || !ville) {
      setError('Veuillez sélectionner une activité et une ville');
      return;
    }

    setLoading(true);
    setError('');
    setResults([]);
    setCurrentPage(1);
    setSearchDone(false);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/fichier-pro/search`, {
        activite,
        ville,
        arrondissement: arrondissement || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000
      });

      setResults(response.data.results || []);
      setTotalResults(response.data.total || 0);
      setSources(response.data.sources || { osm: 0, google: 0 });
      setDbStats(prev => ({ ...prev, total: response.data.totalInDB || prev.total }));
      setSearchDone(true);
      // Refresh le compteur API après recherche
      try {
        const statsRes = await axios.get(`${API_URL}/api/fichier-pro/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (statsRes.data.googleApiCalls !== undefined) {
          setGoogleApiCalls(statsRes.data.googleApiCalls);
          setGoogleApiMois(statsRes.data.googleApiMois || '');
        }
      } catch (e) {}
    } catch (err) {
      const msg = err.response?.data?.error || 'Erreur lors de la recherche. Veuillez réessayer.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Consulter la base locale
  const handleSearchBase = async (pageNum) => {
    const targetPage = typeof pageNum === 'number' ? pageNum : 1;
    setLoading(true);
    setError('');
    if (targetPage === 1) setResults([]);
    setSearchDone(false);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/fichier-pro/base`, {
        activite: activite || undefined,
        ville: ville || undefined,
        search: searchText || undefined,
        statut: filterStatut || undefined,
        page: targetPage,
        limit: ITEMS_PER_PAGE
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });

      setResults(response.data.results || []);
      setTotalResults(response.data.total || 0);
      setCurrentPage(targetPage);
      setBaseTotalPages(response.data.totalPages || 1);
      setSources({ osm: 0, google: 0 });
      setDbStats(response.data.stats || { total: 0, byActivite: [], byVille: [] });
      setSearchDone(true);
    } catch (err) {
      const msg = err.response?.data?.error || 'Erreur lors de la consultation de la base.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRdvModal = (result) => {
    setFormData({
      commercial_id: commerciaux[0]?.id || '',
      titre: `RDV ${activite || result.activite} - ${result.nom}`,
      description: `Source: Fichier Pro (${result.source || 'base'})${result.site_web ? `\nSite web: ${result.site_web}` : ''}${result.horaires ? `\nHoraires: ${result.horaires}` : ''}`,
      client_nom: result.nom,
      client_telephone: result.telephone,
      client_adresse: result.adresse || '',
      date_rdv: new Date().toISOString().split('T')[0],
      heure_debut: '09:00',
      heure_fin: '10:00',
      statut: 'planifie'
    });
    setShowModal(true);
  };

  const handleSubmitRdv = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const dataToSend = {
        ...formData,
        heure_fin: calculateEndTime(formData.heure_debut)
      };
      await axios.post(`${API_URL}/api/planning/rdv`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowModal(false);
      const currentResult = results.find(r =>
        r.nom === formData.client_nom && r.telephone === formData.client_telephone
      );
      if (currentResult) {
        setRdvCreated(prev => ({ ...prev, [currentResult.id || currentResult.telephone_clean]: true }));
      }
    } catch (err) {
      console.error('Erreur creation RDV:', err);
      alert(err.response?.data?.error || 'Erreur lors de la creation du RDV');
    }
  };

  // Pagination
  const totalPages = mode === 'base' ? baseTotalPages : Math.ceil(results.length / ITEMS_PER_PAGE);
  const paginatedResults = mode === 'base' ? results : results.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const availableArrondissements = arrondissementsMap[ville] || [];

  const getSourceBadge = (source) => {
    if (source === 'google') return <span className="source-badge source-google">Google</span>;
    if (source === 'osm+google') return <span className="source-badge source-both">OSM+Google</span>;
    return <span className="source-badge source-osm">OSM</span>;
  };

  return (
    <div className="fichier-pro">
      <div className="fichier-pro-header">
        <h2>Configuration API Google</h2>
        <p className="fichier-pro-subtitle">
          Sources : OpenStreetMap + Google Places | Base locale : <strong>{dbStats.total}</strong> commerçants enregistrés
        </p>
      </div>

      {/* Panneau Configuration API Google - Admin uniquement */}
      {currentUser && currentUser.role === 'admin' && apiUsage && (
        <div className="api-config-panel">
          {/* Section Budget */}
          <div className="api-config-section">
            <h3 className="api-config-title">Budget Google API</h3>
            <div className="api-budget-cards">
              <div className="api-budget-card">
                <div className="api-budget-label">Budget total</div>
                <div className="api-budget-value">${apiUsage.budget.total}</div>
              </div>
              <div className="api-budget-card">
                <div className="api-budget-label">Dépensé</div>
                <div className="api-budget-value" style={{ color: apiUsage.budget.percent > 80 ? '#e53935' : '#2e7d32' }}>
                  ${apiUsage.budget.spent}
                </div>
              </div>
              <div className="api-budget-card">
                <div className="api-budget-label">Restant</div>
                <div className="api-budget-value" style={{ color: apiUsage.budget.remaining < 50 ? '#e53935' : '#1565c0' }}>
                  ${apiUsage.budget.remaining}
                </div>
              </div>
              <div className="api-budget-card">
                <div className="api-budget-label">Utilisation</div>
                <div className="api-budget-value">{apiUsage.budget.percent}%</div>
              </div>
            </div>
            <div className="api-budget-bar-container">
              <div className="api-budget-bar" style={{
                width: `${Math.min(100, apiUsage.budget.percent)}%`,
                background: apiUsage.budget.percent > 90 ? '#e53935' : apiUsage.budget.percent > 70 ? '#ff9800' : '#4caf50'
              }}></div>
            </div>
            {apiUsage.budget.capReached && (
              <div className="api-budget-alert">Budget atteint ! Les appels Google API payants sont suspendus.</div>
            )}
          </div>

          {/* Section Mois en cours */}
          <div className="api-config-section">
            <h3 className="api-config-title">Mois en cours ({apiUsage.currentMonth.mois})</h3>
            <div className="api-month-stats">
              <div className="api-month-stat">
                <span className="api-month-label">Appels totaux</span>
                <span className="api-month-value">{apiUsage.currentMonth.totalCalls.toLocaleString()}</span>
              </div>
              <div className="api-month-stat">
                <span className="api-month-label">Gratuits ({apiUsage.freePerMonth.toLocaleString()}/mois)</span>
                <span className="api-month-value" style={{ color: '#2e7d32' }}>{apiUsage.currentMonth.freeCalls.toLocaleString()}</span>
              </div>
              <div className="api-month-stat">
                <span className="api-month-label">Payants</span>
                <span className="api-month-value" style={{ color: apiUsage.currentMonth.paidCalls > 0 ? '#e53935' : '#666' }}>
                  {apiUsage.currentMonth.paidCalls.toLocaleString()}
                </span>
              </div>
              <div className="api-month-stat">
                <span className="api-month-label">Coût ce mois</span>
                <span className="api-month-value">${apiUsage.currentMonth.cost}</span>
              </div>
            </div>
          </div>

          {/* Historique des mois */}
          {apiUsage.history && apiUsage.history.length > 0 && (
            <div className="api-config-section">
              <h3 className="api-config-title">Historique mensuel</h3>
              <div className="api-history-table">
                <table>
                  <thead>
                    <tr>
                      <th>Mois</th>
                      <th>Appels</th>
                      <th>Gratuits</th>
                      <th>Payants</th>
                      <th>Coût</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiUsage.history.map(h => {
                      const free = Math.min(h.nb_appels, apiUsage.freePerMonth);
                      const paid = Math.max(0, h.nb_appels - apiUsage.freePerMonth);
                      return (
                        <tr key={h.mois}>
                          <td>{h.mois}</td>
                          <td>{h.nb_appels.toLocaleString()}</td>
                          <td style={{ color: '#2e7d32' }}>{free.toLocaleString()}</td>
                          <td style={{ color: paid > 0 ? '#e53935' : '#666' }}>{paid.toLocaleString()}</td>
                          <td>${(paid * apiUsage.costPerCall).toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Section Paramètres */}
          <div className="api-config-section">
            <h3 className="api-config-title">Paramètres</h3>
            <div className="api-config-form">
              <div className="api-config-field">
                <label>Budget total ($)</label>
                <input
                  type="number"
                  value={editBudget}
                  onChange={e => setEditBudget(e.target.value)}
                  min="0"
                  step="1"
                />
                <small>Au-delà des {apiUsage.freePerMonth.toLocaleString()} appels gratuits/mois</small>
              </div>
              <div className="api-config-field">
                <label>Email alerte (90%)</label>
                <input
                  type="email"
                  value={editAlertEmail}
                  onChange={e => setEditAlertEmail(e.target.value)}
                  placeholder="email@exemple.com"
                />
                <small>Alerte envoyée quand 90% du budget est consommé</small>
              </div>
              <button
                className="btn-save-config"
                onClick={handleSaveConfig}
                disabled={savingConfig}
              >
                {savingConfig ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
            </div>
          </div>

          {/* Section Enrichissement */}
          <div className="api-config-section">
            <h3 className="api-config-title">Enrichissement base</h3>
            <div className="api-enrichment-info">
              <div className="api-month-stat">
                <span className="api-month-label">Base locale</span>
                <span className="api-month-value">{apiUsage.totalDB?.toLocaleString()} fiches</span>
              </div>
              <div className="api-month-stat">
                <span className="api-month-label">Combinaisons possibles</span>
                <span className="api-month-value">{apiUsage.totalCombinations} ({apiUsage.nbActivites} activités × {apiUsage.activeVilles?.length} villes)</span>
              </div>
              <div className="api-month-stat">
                <span className="api-month-label">Villes actives</span>
                <span className="api-month-value">{apiUsage.activeVilles?.join(', ')}</span>
              </div>
            </div>

            {populateRunning && apiUsage.populate && (
              <div className="api-populate-progress">
                <div className="api-populate-status">
                  <span>En cours : {apiUsage.populate.current}</span>
                  <span>{apiUsage.populate.done}/{apiUsage.populate.total} ({apiUsage.populate.total > 0 ? Math.round((apiUsage.populate.done / apiUsage.populate.total) * 100) : 0}%)</span>
                </div>
                <div className="api-budget-bar-container">
                  <div className="api-budget-bar" style={{
                    width: `${apiUsage.populate.total > 0 ? Math.round((apiUsage.populate.done / apiUsage.populate.total) * 100) : 0}%`,
                    background: '#1565c0'
                  }}></div>
                </div>
                <div className="api-populate-details">
                  <span>Succès: {apiUsage.populate.successCount}</span>
                  <span>Erreurs: {apiUsage.populate.errorCount}</span>
                  <span>Démarré: {new Date(apiUsage.populate.startedAt).toLocaleTimeString('fr-FR')}</span>
                </div>
              </div>
            )}

            {populateMessage && (
              <div className={`api-populate-message ${populateMessage.startsWith('Erreur') ? 'error' : ''}`}>
                {populateMessage}
              </div>
            )}

            <div className="api-enrichment-controls">
              {!populateRunning ? (
                <button className="btn-start-populate" onClick={handleStartPopulate}>
                  Lancer l'enrichissement
                </button>
              ) : (
                <button className="btn-stop-populate" onClick={handleStopPopulate}>
                  Arrêter l'enrichissement
                </button>
              )}
            </div>
          </div>

          {/* Section Villes autorisées */}
          <div className="api-config-section">
            <h3 className="api-config-title">Villes autorisées pour les agents</h3>
            <div className="villes-config-grid">
              {villesConfig.map(v => (
                <label key={v.ville} className={`ville-checkbox ${v.active ? 'active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={!!v.active}
                    onChange={() => toggleVille(v.ville, v.active)}
                  />
                  {v.ville}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Séparateur avant la recherche */}
      <div style={{ borderTop: '1px solid #e0e0e0', margin: '20px 0' }}></div>

      <h3 style={{ margin: '0 0 10px 0', color: '#1a1a2e' }}>Recherche manuelle</h3>

      {/* Mode recherche uniquement - la base est dans E-Shops Prosper */}

      {/* Filtres */}
      <div className="fichier-pro-filters">
        <div className="filter-group">
          <label>Activité {mode === 'base' ? '(optionnel)' : ''}</label>
          <select value={activite} onChange={e => setActivite(e.target.value)}>
            <option value="">-- {mode === 'base' ? 'Toutes' : 'Choisir'} --</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Ville {mode === 'base' ? '(optionnel)' : ''}</label>
          <select value={ville} onChange={e => { setVille(e.target.value); setArrondissement(''); }}>
            <option value="">-- {mode === 'base' ? 'Toutes' : 'Choisir'} --</option>
            {villesDisponibles.map(v => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </div>

        {mode === 'search' && (
          <div className="filter-group">
            <label>Arrondissement (optionnel)</label>
            <select
              value={arrondissement}
              onChange={e => setArrondissement(e.target.value)}
              disabled={availableArrondissements.length === 0}
            >
              <option value="">-- Tous --</option>
              {availableArrondissements.map(a => (
                <option key={a.code} value={a.code}>{a.label}</option>
              ))}
            </select>
          </div>
        )}

        {mode === 'base' && (
          <div className="filter-group">
            <label>Recherche libre</label>
            <input
              type="text"
              className="filter-input"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Nom, adresse, téléphone..."
              onKeyDown={e => e.key === 'Enter' && handleSearchBase()}
            />
          </div>
        )}
        {mode === 'base' && (
          <div className="filter-group">
            <label>Statut</label>
            <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
              <option value="">-- Tous --</option>
              {Object.entries(statutLabels).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
        )}

        <button
          className="btn-search"
          onClick={mode === 'search' ? handleSearch : handleSearchBase}
          disabled={loading || (mode === 'search' && (!activite || !ville))}
        >
          {loading ? (
            <><span className="spinner-small"></span> Recherche...</>
          ) : mode === 'search' ? (
            'Rechercher'
          ) : (
            'Consulter'
          )}
        </button>
      </div>

      {/* Erreur */}
      {error && (
        <div className="fichier-pro-error">
          {error}
        </div>
      )}

      {/* Résultats */}
      {searchDone && !loading && (
        <div className="fichier-pro-results">
          <div className="results-header">
            <span className="results-count">
              {totalResults} résultat{totalResults !== 1 ? 's' : ''} trouvé{totalResults !== 1 ? 's' : ''}
            </span>
            <div className="results-meta">
              {mode === 'search' && (sources.osm > 0 || sources.google > 0) && (
                <span className="results-sources">
                  OSM: {sources.osm} | Google: {sources.google}
                </span>
              )}
              {totalPages > 1 && (
                <span className="results-page">
                  Page {currentPage} / {totalPages}
                </span>
              )}
            </div>
          </div>

          {results.length > 0 ? (
            <>
              <div className="table-wrapper">
                <table className="fichier-pro-table">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Téléphone</th>
                      <th>Statut</th>
                      <th>Statué par</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedResults.map((r, idx) => (
                      <tr key={r.id || r.telephone_clean || idx}>
                        <td className="td-nom">{r.nom}</td>
                        <td className="td-phone">
                          <a href={`tel:${r.telephone}`} className="phone-link">
                            {r.telephone}
                          </a>
                        </td>
                        <td className="td-statut">
                          <select
                            className={`statut-select statut-${r.statut || 'nouveau'}`}
                            value={r.statut || 'nouveau'}
                            onChange={e => handleStatutChange(r.telephone_clean, e.target.value)}
                          >
                            {Object.entries(statutLabels).map(([key, label]) => (
                              <option key={key} value={key}>{label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="td-statut-par">
                          {r.statut_par_nom && (
                            <span className="statut-par-info">
                              {r.statut_par_nom}
                              <br/>
                              <small>{new Date(r.statut_date).toLocaleDateString('fr-FR')} {new Date(r.statut_date).toLocaleTimeString('fr-FR', {hour:'2-digit', minute:'2-digit'})}</small>
                            </span>
                          )}
                        </td>
                        <td className="td-actions">
                          <button className="btn-voir-fiche" onClick={() => setDetailFiche(r)}>Voir</button>
                          {rdvCreated[r.id || r.telephone_clean] ? (
                            <span className="badge-added">RDV</span>
                          ) : (
                            <button
                              className="btn-add-rdv"
                              onClick={() => handleOpenRdvModal(r)}
                            >
                              RDV
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Modal détail fiche */}
              {detailFiche && (
                <div className="modal-overlay" onClick={() => setDetailFiche(null)}>
                  <div className="modal-content planning-modal" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                      <h3>{detailFiche.nom}</h3>
                      <button className="btn-close" onClick={() => setDetailFiche(null)}>&times;</button>
                    </div>
                    <div className="fiche-detail-body">
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Activité</span>
                        <span className="fiche-value">{detailFiche.activite || '-'}</span>
                      </div>
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Téléphone</span>
                        <span className="fiche-value">
                          <a href={`tel:${detailFiche.telephone}`} className="phone-link">{detailFiche.telephone}</a>
                        </span>
                      </div>
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Adresse</span>
                        <span className="fiche-value">{detailFiche.adresse || '-'}</span>
                      </div>
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Ville</span>
                        <span className="fiche-value">{detailFiche.ville || '-'}</span>
                      </div>
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Code postal</span>
                        <span className="fiche-value">{detailFiche.code_postal || '-'}</span>
                      </div>
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Site web</span>
                        <span className="fiche-value">
                          {detailFiche.site_web ? (
                            <a href={detailFiche.site_web.startsWith('http') ? detailFiche.site_web : `https://${detailFiche.site_web}`} target="_blank" rel="noopener noreferrer" className="web-link">{detailFiche.site_web}</a>
                          ) : '-'}
                        </span>
                      </div>
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Horaires</span>
                        <span className="fiche-value fiche-horaires">{detailFiche.horaires || '-'}</span>
                      </div>
                      {currentUser && currentUser.role === 'admin' && (
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Source</span>
                        <span className="fiche-value">{getSourceBadge(detailFiche.source)}</span>
                      </div>
                      )}
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Statut</span>
                        <span className="fiche-value">
                          <select
                            className={`statut-select statut-${detailFiche.statut || 'nouveau'}`}
                            value={detailFiche.statut || 'nouveau'}
                            onChange={e => {
                              handleStatutChange(detailFiche.telephone_clean, e.target.value);
                              setDetailFiche(prev => ({ ...prev, statut: e.target.value }));
                            }}
                          >
                            {Object.entries(statutLabels).map(([key, label]) => (
                              <option key={key} value={key}>{label}</option>
                            ))}
                          </select>
                        </span>
                      </div>
                      <div className="fiche-detail-actions">
                        {!rdvCreated[detailFiche.id || detailFiche.telephone_clean] && (
                          <button className="btn-add-rdv" onClick={() => { handleOpenRdvModal(detailFiche); setDetailFiche(null); }}>
                            Prendre RDV
                          </button>
                        )}
                      </div>
                      {detailFiche.id && (
                        <div style={{ marginTop: 16 }}>
                          <Encaisser source="recherche_pro" ficheId={detailFiche.id}
                            client={{ nom: detailFiche.nom, entreprise: detailFiche.nom, telephone: detailFiche.telephone, email: detailFiche.email }} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => {
                      const p = Math.max(1, currentPage - 1);
                      if (mode === 'base') handleSearchBase(p);
                      else setCurrentPage(p);
                    }}
                    disabled={currentPage === 1}
                  >
                    Précédent
                  </button>
                  {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                    let page;
                    if (totalPages <= 10) {
                      page = i + 1;
                    } else if (currentPage <= 5) {
                      page = i + 1;
                    } else if (currentPage >= totalPages - 4) {
                      page = totalPages - 9 + i;
                    } else {
                      page = currentPage - 4 + i;
                    }
                    return (
                      <button
                        key={page}
                        className={currentPage === page ? 'active' : ''}
                        onClick={() => {
                          if (mode === 'base') handleSearchBase(page);
                          else setCurrentPage(page);
                        }}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => {
                      const p = Math.min(totalPages, currentPage + 1);
                      if (mode === 'base') handleSearchBase(p);
                      else setCurrentPage(p);
                    }}
                    disabled={currentPage === totalPages}
                  >
                    Suivant
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-results">
              {mode === 'base'
                ? 'Aucun commerçant trouvé dans la base. Lancez des recherches pour alimenter votre base.'
                : 'Aucun résultat trouvé pour cette recherche. Essayez une autre ville ou activité.'}
            </div>
          )}

          {mode === 'search' && (
            <div className="fichier-pro-disclaimer">
              Les données proviennent d'OpenStreetMap et Google Places.
              Les résultats sont automatiquement sauvegardés dans votre base locale.
            </div>
          )}
        </div>
      )}

      {/* Stats base locale en mode base */}
      {mode === 'base' && searchDone && dbStats.byActivite && dbStats.byActivite.length > 0 && (
        <div className="fichier-pro-db-stats">
          <div className="db-stats-section">
            <h4>Par activité</h4>
            <div className="db-stats-tags">
              {dbStats.byActivite.map(s => (
                <span key={s.activite} className="db-stat-tag" onClick={() => { setActivite(s.activite); }}>
                  {s.activite} ({s.count})
                </span>
              ))}
            </div>
          </div>
          {dbStats.byVille && dbStats.byVille.length > 0 && (
            <div className="db-stats-section">
              <h4>Par ville</h4>
              <div className="db-stats-tags">
                {dbStats.byVille.map(s => (
                  <span key={s.ville} className="db-stat-tag" onClick={() => { setVille(s.ville); }}>
                    {s.ville} ({s.count})
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal Nouveau RDV */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content planning-modal">
            <div className="modal-header">
              <h3>Nouveau Rendez-vous</h3>
              <button className="btn-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmitRdv}>
              <SiretLookup
                compact
                onCompanyFound={(data) => {
                  setFormData(prev => ({
                    ...prev,
                    client_nom: data.dirigeant_nom || prev.client_nom,
                    client_adresse: data.adresse || prev.client_adresse,
                    titre: prev.titre || `RDV ${data.entreprise}`,
                    description: prev.description || `Entreprise: ${data.entreprise}${data.dirigeant_qualite ? `\nDirigeant: ${data.dirigeant_nom} (${data.dirigeant_qualite})` : ''}`
                  }));
                }}
              />
              <div className="form-row">
                <div className="form-group">
                  <label>Commercial *</label>
                  <select
                    value={formData.commercial_id}
                    onChange={e => setFormData({...formData, commercial_id: parseInt(e.target.value)})}
                    required
                  >
                    {commerciaux.map(c => (
                      <option key={c.id} value={c.id}>{c.nom}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Statut</label>
                  <select
                    value={formData.statut}
                    onChange={e => setFormData({...formData, statut: e.target.value})}
                  >
                    <option value="planifie">Planifié</option>
                    {(currentUser?.role === 'admin' || currentUser?.id === 2) && (
                      <option value="confirme">Confirmé</option>
                    )}
                    <option value="annule">Annulé</option>
                    <option value="termine">Terminé</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Titre du RDV *</label>
                <input
                  type="text"
                  value={formData.titre}
                  onChange={e => setFormData({...formData, titre: e.target.value})}
                  placeholder="Ex: Presentation produit, Signature contrat..."
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={formData.date_rdv}
                    onChange={e => setFormData({...formData, date_rdv: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Heure *</label>
                  <input
                    type="time"
                    value={formData.heure_debut}
                    onChange={e => setFormData({...formData, heure_debut: e.target.value})}
                    required
                  />
                  <small className="form-hint">Durée: 1h</small>
                </div>
              </div>

              <div className="form-group">
                <label>Nom du client</label>
                <input
                  type="text"
                  value={formData.client_nom}
                  onChange={e => setFormData({...formData, client_nom: e.target.value})}
                  placeholder="Nom du client ou prospect"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Téléphone client</label>
                  <input
                    type="tel"
                    value={formData.client_telephone}
                    onChange={e => setFormData({...formData, client_telephone: e.target.value})}
                    placeholder="06 XX XX XX XX"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse</label>
                  <input
                    type="text"
                    value={formData.client_adresse}
                    onChange={e => setFormData({...formData, client_adresse: e.target.value})}
                    placeholder="Lieu du rendez-vous"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description / Notes</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({...formData, description: e.target.value})}
                  rows="3"
                  placeholder="Détails du rendez-vous..."
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-submit">
                  Créer le RDV
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default FichierPro;
